/**
 * Formatting Utilities
 *
 * Provides display formatters for order enums and values
 */

import type {
  SalesOrderStatus,
  PurchaseOrderStatus,
  BackorderStatus,
  Priority,
  PaymentTerms,
} from '@lognai/plugin-orders';
import { formatDate, formatDateTime, formatRelativeTime } from '@lognai/plugin-sdk';

// Re-export date formatters from plugin-sdk
export { formatDate, formatDateTime, formatRelativeTime };

/**
 * Format sales order status for display
 */
export const formatSalesOrderStatus = (status: SalesOrderStatus): string => {
  const statusMap: Record<SalesOrderStatus, string> = {
    draft: 'Draft',
    pending: 'Pending',
    confirmed: 'Confirmed',
    processing: 'Processing',
    partially_fulfilled: 'Partially Fulfilled',
    fulfilled: 'Fulfilled',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  };
  return statusMap[status] || status;
};

/**
 * Format purchase order status for display
 */
export const formatPurchaseOrderStatus = (status: PurchaseOrderStatus): string => {
  const statusMap: Record<PurchaseOrderStatus, string> = {
    draft: 'Draft',
    submitted: 'Submitted',
    confirmed: 'Confirmed',
    partially_received: 'Partially Received',
    received: 'Received',
    cancelled: 'Cancelled',
  };
  return statusMap[status] || status;
};

/**
 * Format backorder status for display
 */
export const formatBackorderStatus = (status: BackorderStatus): string => {
  const statusMap: Record<BackorderStatus, string> = {
    pending: 'Pending',
    partial_available: 'Partial Available',
    available: 'Available',
    allocated: 'Allocated',
    fulfilled: 'Fulfilled',
    cancelled: 'Cancelled',
  };
  return statusMap[status] || status;
};

/**
 * Format priority for display
 */
export const formatPriority = (priority: Priority): string => {
  const priorityMap: Record<Priority, string> = {
    low: 'Low',
    normal: 'Normal',
    high: 'High',
    urgent: 'Urgent',
  };
  return priorityMap[priority] || priority;
};

/**
 * Format payment terms for display
 */
export const formatPaymentTerms = (terms: PaymentTerms): string => {
  const termsMap: Record<PaymentTerms, string> = {
    net_30: 'Net 30',
    net_60: 'Net 60',
    net_90: 'Net 90',
    cod: 'Cash on Delivery',
    prepaid: 'Prepaid',
    custom: 'Custom',
  };
  return termsMap[terms] || terms;
};

/**
 * Format currency amount
 */
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
};

/**
 * Format quantity
 */
export const formatQuantity = (quantity: number): string => {
  return new Intl.NumberFormat('en-US').format(quantity);
};

/**
 * Format percentage
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Format allocation status for display
 */
export const formatAllocationStatus = (status: 'none' | 'partial' | 'full'): string => {
  const statusMap: Record<string, string> = {
    none: 'Not Allocated',
    partial: 'Partially Allocated',
    full: 'Fully Allocated',
  };
  return statusMap[status] || status;
};
